import { useEffect, useId, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import { ChevronLeft, Signpost } from "lucide-react";
import { Button } from "@/components/design/Button";
import type { FollowUpView, OptionView, StoryLanguage } from "@/lib/games/roleplay/contract";

// The scene's clickable courses of action (play contract §3.5; agent-service#129, spark#1075), always two levels: the
// options, and in place of them the follow-ups of the one opened, with „Zurück“ back to the options. A click never
// sends anything: an option only opens its follow-ups, and a follow-up hands its template to the field (TurnInput
// writes it, options.ts says how). Neutral chips on the plain surface: nothing is coloured or outlined to show what is
// open or what was picked, the panel's heading names the open option and the field shows what was inserted.
//
// Focus: opening an option moves it to the first follow-up, „Zurück“ back to the option that was opened. When a move
// takes away the option whose panel holds the focus, the panel closes under it and the focus would fall to the page;
// it goes to the first option the view now holds instead, or to the field (`onFocusLost`) when none is left. While the
// guide shows them outside its move step (`disabled`) every button is switched off; in the move step the player tries
// them as in the game (spark#1236).

type Focus = { to: "followUps" } | { to: "option"; id: string } | { to: "first" } | null;

// Whether the focus has fallen out of the document's elements: to the page itself, or with an element that is gone.
function focusLost(): boolean {
  const active = document.activeElement;
  return active === null || active === document.body || !active.isConnected;
}

export function CourseOptions({
  options,
  language,
  disabled = false,
  onPick,
  onBack,
  onFocusLost,
}: {
  options: readonly OptionView[];
  language: StoryLanguage;
  disabled?: boolean;
  onPick: (followUp: FollowUpView) => void;
  onBack: (option: OptionView) => void;
  onFocusLost?: () => void;
}) {
  const label = useId();
  const heading = useId();
  const [openId, setOpenId] = useState<string | null>(null);
  const focus = useRef<Focus>(null);
  const root = useRef<HTMLDivElement>(null);
  // Whether the focus is in the open panel. A blur towards nothing (a click on the plain page) keeps it, since the focus
  // then has no place a closing panel could take from it either.
  const held = useRef(false);
  // The list follows the story (play contract §3.5): after any move the view may hold other options. An option that is
  // no longer offered is not open, so the panel shows the options again, and it stays closed should that option come
  // back later. Option ids are `a` and digits (contract.ts), so one goes into a selector as it is.
  const open = options.find((option) => option.id === openId) ?? null;

  useEffect(() => {
    if (openId === null || options.some((option) => option.id === openId)) return;
    if (held.current && focusLost()) focus.current = { to: "first" };
    held.current = false;
    setOpenId(null);
  }, [options, openId]);

  useEffect(() => {
    const target = focus.current;
    focus.current = null;
    if (!target) return;
    const selector =
      target.to === "followUps" ? "[data-follow-up]" : target.to === "first" ? "[data-option]" : `[data-option="${target.id}"]`;
    const button = root.current?.querySelector<HTMLButtonElement>(selector);
    if (button) button.focus();
    else if (target.to === "first") onFocusLost?.();
  }, [openId, onFocusLost]);

  if (options.length === 0) return null;

  return (
    <div ref={root} className="mt-6">
      <p id={label} className="flex items-center gap-1.5 smycles-field-label">
        <Signpost aria-hidden="true" className="size-3.5 shrink-0 text-ink-muted" strokeWidth={1.6} />
        <FormattedMessage id="grow.roleplay.options.label" defaultMessage="Courses of action" />
      </p>
      {open ? (
        <div
          className="mt-1.5"
          onFocus={() => {
            held.current = true;
          }}
          onBlur={(event) => {
            const next = event.relatedTarget;
            if (next !== null && !event.currentTarget.contains(next)) held.current = false;
          }}
        >
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <Button
              variant="ghost"
              size="sm"
              disabled={disabled}
              className="-ml-3 gap-1"
              onClick={() => {
                focus.current = { to: "option", id: open.id };
                held.current = false;
                setOpenId(null);
                onBack(open);
              }}
            >
              <ChevronLeft aria-hidden="true" className="size-3.5" strokeWidth={1.6} />
              <FormattedMessage id="grow.roleplay.options.back" defaultMessage="Back" />
            </Button>
            <p id={heading} className="min-w-0 text-smycles-label text-ink">
              {open.label[language]}
            </p>
          </div>
          <div role="group" aria-labelledby={heading} className="mt-2">
            <ul className="flex flex-wrap gap-2">
              {open.followUps.map((followUp) => (
                <li key={followUp.id} className="min-w-0">
                  <Button
                    variant="chip"
                    size="sm"
                    disabled={disabled}
                    data-follow-up=""
                    className="text-left whitespace-normal"
                    onClick={() => onPick(followUp)}
                  >
                    {followUp.label[language]}
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      ) : (
        <ul aria-labelledby={label} className="mt-1.5 flex flex-wrap gap-2">
          {options.map((option) => (
            <li key={option.id} className="min-w-0">
              <Button
                variant="chip"
                size="sm"
                disabled={disabled}
                data-option={option.id}
                className="text-left whitespace-normal"
                onClick={() => {
                  focus.current = { to: "followUps" };
                  setOpenId(option.id);
                }}
              >
                {option.label[language]}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
